import React, { useEffect, useState } from "react";
import { UserPlus, Pencil, Trash2, KeyRound, RefreshCw, Search, Users, ArrowRightLeft, UserMinus, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatCard, Modal, Select, toast, Avatar, Spinner } from "../components/ui";
import { ROLE_META, money } from "../lib/constants";

const ROLES = [{ value: "agent", label: "Agent" }, { value: "team_leader", label: "Team Leader" }, { value: "admin", label: "Admin" }];

export default function Agents() {
  const { user } = useAuth();
  const [data, setData] = useState({ users: [], unassigned: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [bulk, setBulk] = useState(null); // 'assign' | 'reassign' | 'deassign'

  const load = async () => { setLoading(true); try { const r = await api.get("/users"); setData(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const del = async (u) => { if (!window.confirm(`Delete ${u.full_name}? Their clients will be unassigned.`)) return; try { await api.delete(`/users/${u.id}`); toast("User deleted"); load(); } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } };
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const filtered = data.users.filter((u) => !q || u.full_name.toLowerCase().includes(q.toLowerCase()) || u.username.includes(q.toLowerCase()));
  const allSel = filtered.length > 0 && filtered.every((u) => selected.has(u.id));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900 dark:text-white">Manage Users</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Create, edit, and manage user accounts</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className="btn-secondary" onClick={() => selected.size ? setBulk("assign") : toast("Select agent(s) first", "error")} data-testid="bulk-assign"><UserPlus className="w-4 h-4" /> Bulk Assign</button>
          <button className="btn-secondary" onClick={() => selected.size ? setBulk("reassign") : toast("Select agent(s) first", "error")} data-testid="bulk-reassign"><ArrowRightLeft className="w-4 h-4" /> Bulk Reassign</button>
          <button className="btn-secondary" onClick={() => selected.size ? setBulk("deassign") : toast("Select agent(s) first", "error")} data-testid="bulk-deassign"><UserMinus className="w-4 h-4" /> Bulk Deassign</button>
          <button className="btn-secondary" onClick={load} data-testid="agents-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button className="btn-primary" onClick={() => setAddOpen(true)} data-testid="add-user-btn"><UserPlus className="w-4 h-4" /> Add User</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Agents" value={data.users.length} icon={Users} testid="agents-total" />
        <StatCard label="Unassigned" value={data.unassigned.toLocaleString()} accent="text-amber-600 dark:text-amber-400" testid="agents-unassigned" />
        <StatCard label="Admins" value={data.users.filter((u) => u.role === "admin").length} accent="text-indigo-600 dark:text-indigo-400" />
        <StatCard label="Selected" value={selected.size} accent="text-cyan-600 dark:text-cyan-400" />
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input className="input pl-9" placeholder="Search users..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
          <input type="checkbox" checked={allSel} onChange={() => setSelected(allSel ? new Set() : new Set(filtered.map((u) => u.id)))} data-testid="select-all-agents" /> Select all
        </label>
      </div>

      {loading ? <div className="py-12 text-center"><Spinner className="w-6 h-6 inline text-slate-400" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((u) => <AgentCard key={u.id} u={u} me={user} selected={selected.has(u.id)} onToggle={() => toggle(u.id)} onEdit={() => setEditUser(u)} onPw={() => setPwUser(u)} onDel={() => del(u)} onChanged={load} />)}
        </div>
      )}

      {addOpen && <UserModal title="Add User" onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />}
      {editUser && <UserModal title="Edit User" user={editUser} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} />}
      {pwUser && <ResetPwModal user={pwUser} onClose={() => setPwUser(null)} />}
      {bulk && <BulkModal type={bulk} agents={data.users} selected={[...selected]} onClose={() => setBulk(null)} onDone={() => { setBulk(null); setSelected(new Set()); load(); }} />}
    </div>
  );
}

function AgentCard({ u, me, selected, onToggle, onEdit, onPw, onDel, onChanged }) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState(null);
  const expand = async () => {
    setOpen((o) => !o);
    if (clients === null) { const r = await api.get(`/users/${u.id}/clients`); setClients(r.data); }
  };
  return (
    <div className={`card p-5 ${selected ? "ring-2 ring-indigo-500" : ""}`} data-testid={`agent-card-${u.username}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <input type="checkbox" checked={selected} onChange={onToggle} data-testid={`select-agent-${u.username}`} />
          <Avatar name={u.full_name} size="h-11 w-11" />
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 dark:text-white truncate">{u.full_name}{u.id === me.id && <span className="text-xs text-slate-400"> (You)</span>}</div>
            <div className="text-xs text-slate-400 truncate">{u.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className={`badge ${ROLE_META[u.role]?.cls || ROLE_META.agent.cls}`}>{ROLE_META[u.role]?.label || u.role}</span>
          <button className="btn-ghost p-1.5" onClick={onPw} title="Reset password" data-testid={`reset-pw-${u.username}`}><KeyRound className="w-4 h-4" /></button>
          <button className="btn-ghost p-1.5" onClick={onEdit} title="Edit" data-testid={`edit-user-${u.username}`}><Pencil className="w-4 h-4" /></button>
          {u.id !== me.id && <button className="btn-ghost p-1.5 text-rose-500" onClick={onDel} title="Delete" data-testid={`del-user-${u.username}`}><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-4 text-center">
        {[["Leads", u.leads.toLocaleString(), "text-blue-600 dark:text-blue-400"], ["Clients", u.clients, "text-emerald-600 dark:text-emerald-400"], ["Deposits", money(u.deposits), "text-amber-600 dark:text-amber-400"], ["Conversions", u.conversions, "text-cyan-600 dark:text-cyan-400"]].map(([l, v, cls]) => (
          <div key={l} className="rounded-lg bg-slate-50 dark:bg-slate-800/40 py-2"><div className={`text-base font-bold font-display ${cls}`}>{v}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div></div>
        ))}
      </div>
      <button className="mt-3 w-full flex items-center justify-between text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200" onClick={expand} data-testid={`expand-${u.username}`}>
        <span>Assigned Clients ({u.clients})</span><ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {clients === null ? <Spinner className="w-4 h-4 text-slate-400" /> : clients.length === 0 ? <div className="text-xs text-slate-400 py-2">No clients assigned</div> :
            clients.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border border-slate-100 dark:border-slate-800 px-3 py-1.5 text-sm">
                <span className="text-slate-700 dark:text-slate-200 truncate">{c.name}</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">{money(c.balance)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function BulkModal({ type, agents, selected, onClose, onDone }) {
  const [count, setCount] = useState(50);
  const [toAgent, setToAgent] = useState("");
  const [clientType, setClientType] = useState("lead");
  const [busy, setBusy] = useState(false);
  const names = agents.filter((a) => selected.includes(a.id)).map((a) => a.full_name).join(", ");
  const submit = async () => {
    setBusy(true);
    try {
      if (type === "assign") { const r = await api.post("/users/bulk-assign-leads", { agent_ids: selected, count: Number(count), client_type: clientType }); toast(`Assigned ${r.data.assigned} ${clientType}s`); }
      else if (type === "deassign") { const r = await api.post("/users/bulk-deassign", { agent_ids: selected }); toast(`Deassigned ${r.data.modified} contacts`); }
      else if (type === "reassign") {
        if (!toAgent) { setBusy(false); return toast("Choose a destination agent", "error"); }
        let total = 0;
        for (const from of selected) { if (from === toAgent) continue; const r = await api.post("/users/bulk-reassign", { from_agent_id: from, to_agent_id: toAgent }); total += r.data.modified; }
        toast(`Reassigned ${total} contacts`);
      }
      onDone();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); }
  };
  const title = type === "assign" ? "Bulk Assign Leads" : type === "reassign" ? "Bulk Reassign" : "Bulk Deassign";
  return (
    <Modal open onClose={onClose} title={title} testid="bulk-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy} data-testid="bulk-submit">Apply</button></>}>
      <div className="text-sm text-slate-500 dark:text-slate-400">Selected agents: <b className="text-slate-700 dark:text-slate-200">{names}</b></div>
      {type === "assign" && (
        <>
          <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Contact type</label>
            <Select value={clientType} onChange={setClientType} options={[{ value: "lead", label: "Leads" }, { value: "client", label: "Clients" }]} testid="bulk-type" /></div>
          <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Count per agent (from unassigned pool)</label>
            <input type="number" className="input mt-1" value={count} onChange={(e) => setCount(e.target.value)} data-testid="bulk-count" /></div>
        </>
      )}
      {type === "reassign" && (
        <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Reassign all their contacts to</label>
          <Select value={toAgent} onChange={setToAgent} placeholder="Choose agent" options={agents.map((a) => ({ value: a.id, label: a.full_name }))} testid="bulk-to-agent" /></div>
      )}
      {type === "deassign" && <div className="text-sm text-slate-500 dark:text-slate-400">This will unassign ALL leads and clients currently assigned to the selected agent(s).</div>}
    </Modal>
  );
}

function UserModal({ title, user, onClose, onDone }) {
  const editing = !!user;
  const [form, setForm] = useState({ username: user?.username || "", full_name: user?.full_name || "", role: user?.role || "agent", password: "", email: user?.email || "" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/users/${user.id}`, { full_name: form.full_name, role: form.role, email: form.email });
      else { if (!form.username || !form.full_name || !form.password) { setBusy(false); return toast("Fill all required fields", "error"); } await api.post("/users", form); }
      toast("Saved"); onDone();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={title} testid="user-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy} data-testid="user-save">Save</button></>}>
      {!editing && <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Username</label><input className="input mt-1" value={form.username} onChange={(e) => upd("username", e.target.value)} data-testid="user-username" /></div>}
      <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Full name</label><input className="input mt-1" value={form.full_name} onChange={(e) => upd("full_name", e.target.value)} data-testid="user-fullname" /></div>
      <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Role</label><Select value={form.role} onChange={(v) => upd("role", v)} options={ROLES} testid="user-role" /></div>
      {!editing && <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Password</label><input type="text" className="input mt-1" value={form.password} onChange={(e) => upd("password", e.target.value)} data-testid="user-password" /></div>}
    </Modal>
  );
}

function ResetPwModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await api.put(`/users/${user.id}/password`, { new_password: pw }); toast("Password reset"); onClose(); } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); } };
  return (
    <Modal open onClose={onClose} title={`Reset password — ${user.full_name}`} testid="reset-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy || !pw} data-testid="reset-submit">Reset</button></>}>
      <input type="text" className="input" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw-input" />
    </Modal>
  );
}
